import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  coverageCasePath,
  listCoverageCases,
  syncCoverageCasesFromPrescriptions,
} from "@/core/insurance/coverage-case-store";
import { getInsurerById } from "@/core/insurance/insurer-store";
import {
  COVERAGE_CASE_KIND_LABELS,
  COVERAGE_CASE_STATUS_LABELS,
} from "@/core/insurance/types";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { routes } from "@/router/all_routes";

export function InsuranceCasesPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    syncCoverageCasesFromPrescriptions();
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:coverage-cases", refresh);
    window.addEventListener("hospitol:clinical-prescriptions", refresh);
    return () => {
      window.removeEventListener("hospitol:coverage-cases", refresh);
      window.removeEventListener("hospitol:clinical-prescriptions", refresh);
    };
  }, []);

  const cases = useMemo(() => {
    void refreshKey;
    const list = listCoverageCases();
    if (statusFilter === "all") return list;
    return list.filter((c) => c.status === statusFilter);
  }, [refreshKey, statusFilter]);

  const pagination = useClientPagination(cases, [statusFilter], 25);
  const { pagedItems } = pagination;

  return (
    <div>
      <PageHeader
        title="Dossiers de prise en charge"
        description="Consultations et ordonnances soumises aux assureurs — validation des traitements."
        actions={
          <Link to={routes.insuranceHome} className="btn btn-outline-secondary btn-sm">
            Assurance médicale
          </Link>
        }
      />

      <div className="ph-invoice-sheet ph-lab-filters mb-3">
        <select
          className="form-select form-select-sm w-100 w-md-auto ph-lab-filter-select"
          style={{ maxWidth: "14rem" }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tous les statuts</option>
          {Object.entries(COVERAGE_CASE_STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="ph-invoice-sheet">
        {cases.length === 0 ? (
          <p className="text-muted mb-0">Aucun dossier — associez des patients à un assureur.</p>
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Patient</th>
                  <th>Assureur</th>
                  <th>Type</th>
                  <th>Libellé</th>
                  <th>Statut</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((c) => {
                  const patient = getPatientById(c.patientId);
                  const insurer = getInsurerById(c.insurerId);
                  return (
                    <tr key={c.id} className="ph-diag-row">
                      <td data-label="Date" className="small text-muted">
                        {new Date(c.createdAt).toLocaleDateString("fr-FR")}
                      </td>
                      <td data-label="Patient">
                        {patient ? patientFullName(patient) : "—"}
                      </td>
                      <td data-label="Assureur" className="small">
                        {insurer?.name ?? "—"}
                      </td>
                      <td data-label="Type" className="small">
                        {COVERAGE_CASE_KIND_LABELS[c.kind]}
                      </td>
                      <td data-label="Libellé">{c.label}</td>
                      <td data-label="Statut">
                        <span className={`hosp-ins-badge hosp-ins-badge--${c.status}`}>
                          {COVERAGE_CASE_STATUS_LABELS[c.status]}
                        </span>
                      </td>
                      <td data-label="Action" className="text-end">
                        <Link to={coverageCasePath(c.id)} className="btn btn-link btn-sm p-0">
                          Ouvrir
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <TablePagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pagination.totalItems}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </div>
        )}
      </div>
    </div>
  );
}
